import React, { useEffect } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Card, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ReactComponent as Car } from 'src/Resources/Images/VehicleIntegration/carUnderCloth.svg'
import { trim } from 'lodash'
import { useUser } from 'src/Hooks/useUser'
import Vehicles from './Vehicles'
import useUserVehicles from '../hooks/use-user-vehicles'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { VehiclesLoading } from './loading'
import { useRouting, useShowInstallationNotConfigured } from '../hooks'
import { useParams } from 'react-router-dom'
import { useLoadDashboardData } from 'src/Libs/dashboard/hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { REAUTHENTICATE_LOCAL_STORAGE_KEY } from '../consts'
import { useUserRoles } from 'src/Hooks/user'

const useStyles = makeStyles()(theme => ({
  vehicleCardButton: {
    display: 'flex',
    margin: '25px auto 20px',
  },
  vehicleCardHeadline: {
    marginTop: '20px',
    marginBottom: '10px',
    textAlign: 'center',
  },
  vehicleCardText: {
    margin: '0px 25px',
    textAlign: 'center',
  },
}))

/*
 * Starting page of our vehicle integration. Should be replaced when vehicles are already connected.
 */
const VehicleIntegration: React.FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { userData } = useUser()
  const greeting = trim(`${t('emobility.vehicle-integration.greeting')} ${userData?.Vorname}`) + '!'
  const { navigateToAddVehiclePage } = useRouting()
  const { installationId } = useParams<{ installationId: string }>()
  const { vehicles, loading, hasError, refetchVehicles } = useUserVehicles(Number(installationId))
  const { hasGpsCoordinates } = useShowInstallationNotConfigured(installationId)
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  /* we need to load dashboard data so that we have updated information about the installation and storage */
  useLoadDashboardData({ installationId, storageId })
  const { isServiceUser } = useUserRoles()

  const vehicleIdReauth = localStorage.getItem(REAUTHENTICATE_LOCAL_STORAGE_KEY)

  useEffect(() => {
    /* after re-authentication we need to refetch the vehicles to show the updated list */
    if (vehicleIdReauth) {
      refetchVehicles()

      /* clear local storage */
      localStorage.removeItem(REAUTHENTICATE_LOCAL_STORAGE_KEY)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleIdReauth])

  const handleAddVehicleButtonClick = (): void => {
    navigateToAddVehiclePage()
  }

  if (loading) {
    return <VehiclesLoading />
  }

  if (vehicles && vehicles.length > 0) {
    return <Vehicles installationId={installationId} vehicles={vehicles} />
  }

  return (
    <>
      {hasError && (
        <Box sx={{ width: '100%', pb: 2 }}>
          <Alert severity="error" text={t('emobility.vehicle-integration.vehicles.fetching-failed')} />
        </Box>
      )}
      <Card>
        <Typography variant="h1" className={classes.vehicleCardHeadline}>
          {greeting}
        </Typography>
        <Typography variant="body2" className={classes.vehicleCardText}>
          {t('emobility.vehicle-integration.description')}
        </Typography>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '44px auto 120px auto' }}>
          <Car />
        </Box>
        {!hasGpsCoordinates && (
          <Typography sx={{ maxWidth: '600px', textAlign: 'center', display: 'flex', marginRight: 'auto', marginLeft: 'auto' }} variant="body2">
            {t('emobility.vehicle-integration.installation-missing-coordinates')}
          </Typography>
        )}
        <Button
          /* service users should not be able to add vehicles on their own */
          disabled={!hasGpsCoordinates || isServiceUser}
          className={classes.vehicleCardButton}
          onClick={handleAddVehicleButtonClick}
          variant="contained"
        >
          {t('emobility.vehicle-integration.add-vehicle')}
        </Button>
      </Card>
    </>
  )
}

export default VehicleIntegration
